import { useState, useEffect } from "react";

import { formatDistanceToNow } from "date-fns";
import { Sparkles, Loader2, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { Textarea } from "@/components/ui/textarea";
import { SaveIndicator } from "@/components/wizard/SaveIndicator";
import { useEdgeFunction } from "@/hooks/useEdgeFunction";
import { useFormPersistence } from "@/hooks/useFormPersistence";

type PrebuiltPersona = {
  role_name: string;
  mission: string;
  principles: string;
  competencies: string;
  key_relationships: string;
  typical_questions: string;
};

interface PersonaEditorProps {
  persona: PrebuiltPersona;
  onChange: (persona: PrebuiltPersona) => void;
  onDelete?: () => void;
  boardContext?: string;
}

export const PersonaEditor = ({ persona, onChange, onDelete, boardContext }: PersonaEditorProps) => {
  const { execute: invoke, isLoading } = useEdgeFunction({ auth: true });
  const [enhancing, setEnhancing] = useState<string | null>(null);

  // Form persistence for persona editing
  const {
    values: formValues,
    updateValues,
    lastSaved,
    hasSavedData,
    clearFormData,
  } = useFormPersistence(
    persona,
    {
      formId: `persona-editor-${persona.role_name || 'new'}`,
      enabled: true,
      debounceMs: 1000,
    }
  );

  // Sync form values with parent on change
  useEffect(() => {
    if (JSON.stringify(formValues) !== JSON.stringify(persona)) {
      onChange(formValues as PrebuiltPersona);
    }
  }, [formValues]);

  const enhanceField = async (field: keyof PrebuiltPersona, fieldLabel: string) => {
    setEnhancing(field);
    
    const { data, error } = await invoke("enhance-board-content", {
      body: {
        type: field === "typical_questions" ? "persona_questions" : "persona_field",
        content: persona[field] as string,
        context: field === "typical_questions" ? persona.role_name : boardContext,
        fieldName: fieldLabel,
      },
      showErrorToast: true,
    });

    setEnhancing(null);
    
    if (error) return;

    updateValues({ [field]: data.enhanced.trim() } as Partial<PrebuiltPersona>);
    
    toast.success(`${fieldLabel} enhanced!`);
  };

  const updateField = (field: keyof PrebuiltPersona, value: string | string[]) => {
    updateValues({ [field]: value } as Partial<PrebuiltPersona>);
  };


  return (
    <>
      <SaveIndicator lastSaved={lastSaved} />
      <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <CardTitle>{formValues.role_name || "New Persona"}</CardTitle>
            {hasSavedData && lastSaved && (
              <p className="text-xs text-muted-foreground mt-1">
                Draft saved {formatDistanceToNow(lastSaved, { addSuffix: true })}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {hasSavedData && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  clearFormData();
                  toast.success("Draft cleared");
                }}
                className="text-muted-foreground"
              >
                Clear Draft
              </Button>
            )}
            {onDelete && (
              <Button variant="ghost" size="sm" onClick={onDelete}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Role Name</Label>
          </div>
          <Input
            value={formValues.role_name}
            onChange={(e) => updateField("role_name", e.target.value)}
            placeholder="e.g., Chief Marketing Officer (CMO)"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Mission</Label>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => enhanceField("mission", "Mission")}
              disabled={isLoading}
            >
              {enhancing === "mission" ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Sparkles className="h-3 w-3" />
              )}
            </Button>
          </div>
          <Textarea
            value={formValues.mission}
            onChange={(e) => updateField("mission", e.target.value)}
            placeholder="Define the persona's primary mission..."
            rows={2}
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Principles</Label>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => enhanceField("principles", "Principles")}
              disabled={isLoading}
            >
              {enhancing === "principles" ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Sparkles className="h-3 w-3" />
              )}
            </Button>
          </div>
          <Textarea
            value={formValues.principles}
            onChange={(e) => updateField("principles", e.target.value)}
            placeholder="Core principles this role follows..."
            rows={2}
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Competencies</Label>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => enhanceField("competencies", "Competencies")}
              disabled={isLoading}
            >
              {enhancing === "competencies" ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Sparkles className="h-3 w-3" />
              )}
            </Button>
          </div>
          <Textarea
            value={formValues.competencies}
            onChange={(e) => updateField("competencies", e.target.value)}
            placeholder="Key skills and areas of expertise..."
            rows={2}
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Key Relationships</Label>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => enhanceField("key_relationships", "Key Relationships")}
              disabled={isLoading}
            >
              {enhancing === "key_relationships" ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Sparkles className="h-3 w-3" />
              )}
            </Button>
          </div>
          <Textarea
            value={formValues.key_relationships}
            onChange={(e) => updateField("key_relationships", e.target.value)}
            placeholder="How this role collaborates with others..."
            rows={2}
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Typical Questions</Label>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => enhanceField("typical_questions", "Typical Questions")}
                disabled={isLoading}
              >
                {enhancing === "typical_questions" ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Sparkles className="h-3 w-3" />
                )}
              </Button>
            </div>
          </div>
          <Textarea
            value={formValues.typical_questions}
            onChange={(e) => updateField("typical_questions", e.target.value)}
            placeholder="Enter typical questions, one per line..."
            rows={4}
          />
        </div>
      </CardContent>
      </Card>
    </>
  );
};
